/**
 * Doctors API Service
 * Handles all doctor-related API calls
 */

import axios from '@/config/axios';
import type {
  Doctor,
  CreateDoctorDto,
  UpdateDoctorDto,
  PaginatedDoctorsResponse,
  DoctorSchedule,
  UpdateDoctorScheduleDto,
  DoctorAvailability,
} from '@/types/api-types';

export interface ListDoctorsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  specialty?: string;
  department?: string;
  status?: 'active' | 'on-leave' | 'inactive';
  isAvailableToday?: boolean;
  languages?: string;
  minExperienceYears?: number;
}

export interface CheckAvailabilityParams {
  date: string;
  duration?: number;
}

export type DoctorDayAvailability = DoctorAvailability;

export interface NextAvailableDateParams {
  /** The browser's wall-clock `YYYY-MM-DDTHH:mm`; on that day only slots starting after it count. */
  from: string;
  duration?: number;
}

export interface DoctorNextAvailableDate {
  doctorId: string;
  /** `YYYY-MM-DD`, or null when the doctor has no free slot in the search window. */
  date: string | null;
  /** Length of the search window in days, counting the `from` date. */
  windowDays: number;
}

/**
 * Doctors API
 */
export const doctorsApi = {
  /**
   * List doctors with pagination and filters
   */
  async list(params?: ListDoctorsParams): Promise<PaginatedDoctorsResponse> {
    const response = await axios.get('/doctors', { params });
    return response.data;
  },

  /**
   * Get doctor by ID
   */
  async getById(id: string): Promise<Doctor> {
    const response = await axios.get(`/doctors/${id}`);
    return response.data;
  },

  /**
   * Get the doctor profile for the currently authenticated user.
   * Requires role=DOCTOR on the caller.
   */
  async getMyProfile(): Promise<Doctor> {
    const response = await axios.get('/doctors/me');
    return response.data;
  },

  /**
   * Create new doctor
   */
  async create(data: CreateDoctorDto): Promise<Doctor> {
    const response = await axios.post('/doctors', data);
    return response.data;
  },

  /**
   * Update doctor
   */
  async update(id: string, data: UpdateDoctorDto): Promise<Doctor> {
    const response = await axios.put(`/doctors/${id}`, data);
    return response.data;
  },

  /**
   * Delete doctor (soft delete)
   */
  async delete(id: string): Promise<void> {
    await axios.delete(`/doctors/${id}`);
  },

  /**
   * Search doctors by name or specialty
   */
  async search(query: string): Promise<Doctor[]> {
    const response = await axios.get('/doctors', {
      params: { search: query },
    });
    return response.data.items;
  },

  // ============================================================================
  // Schedule Management
  // ============================================================================

  /**
   * Get doctor's schedule
   */
  async getSchedule(id: string): Promise<DoctorSchedule> {
    const response = await axios.get(`/doctors/${id}/schedule`);
    return response.data;
  },

  /**
   * Update doctor's schedule
   */
  async updateSchedule(id: string, schedule: UpdateDoctorScheduleDto): Promise<DoctorSchedule> {
    const response = await axios.put(`/doctors/${id}/schedule`, schedule);
    return response.data;
  },

  /**
   * Check doctor's availability for a specific date/time
   */
  async getAvailability(id: string, params: CheckAvailabilityParams): Promise<DoctorDayAvailability> {
    const response = await axios.get(`/doctors/${id}/availability`, { params });
    return response.data;
  },

  /**
   * First date, from `from` on, with a free slot for the doctor
   */
  async getNextAvailableDate(id: string, params: NextAvailableDateParams): Promise<DoctorNextAvailableDate> {
    const response = await axios.get<DoctorNextAvailableDate>(`/doctors/${id}/next-available`, { params });
    return response.data;
  },

  // ============================================================================
  // Specialty & Department
  // ============================================================================

  /**
   * Get doctors by specialty
   */
  async getBySpecialty(specialty: string, params?: Omit<ListDoctorsParams, 'specialty'>): Promise<PaginatedDoctorsResponse> {
    const response = await axios.get('/doctors', {
      params: { ...params, specialty },
    });
    return response.data;
  },

  /**
   * Get doctors by department
   */
  async getByDepartment(department: string, params?: Omit<ListDoctorsParams, 'department'>): Promise<PaginatedDoctorsResponse> {
    const response = await axios.get('/doctors', {
      params: { ...params, department },
    });
    return response.data;
  },
};

export default doctorsApi;
